import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, MapPinIcon, ChevronDownIcon, PhoneIcon, UsersIcon, PhoneOffIcon, CalendarXIcon, TrendingDownIcon, BarChart3Icon, BuildingIcon, LockIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
const faqs = [{
  question: 'What does Viridia Analytics actually do for home service businesses?',
  answer: 'We build predictable growth engines for HVAC, painting, landscaping, and contractor businesses across Phoenix. Our core focus is speed-to-lead: we use AI-assisted automation to respond to inquiries instantly, manage targeted ad campaigns, and build conversion-focused websites to turn more traffic into booked appointments.'
}, {
  question: 'Do you help with lead follow-up after ads start running?',
  answer: 'Yes. That is our primary differentiator. We do not just generate clicks and walk away. We implement AI systems that respond to text messages, lead platform inquiries, and web forms in under 60 seconds, 24/7, ensuring leads do not go cold.'
}, {
  question: 'What is AI lead conversion?',
  answer: 'It is a system where an AI assistant handles initial inbound communication. When a lead reaches out, the AI instantly replies, answers common questions, qualifies the prospect based on your services, and can even book the estimate directly on your calendar.'
}, {
  question: 'Can you work with my current CRM?',
  answer: 'In many cases, yes. We build custom automations to connect your existing tools. If your current CRM is holding you back, we can also migrate you to a centralized command center that handles pipeline visibility, invoicing, and communications in one place.'
}, {
  question: 'Do you only work with Phoenix businesses?',
  answer: 'We are headquartered in Phoenix, Arizona, and specialize in this local market. However, we also partner with home service businesses remotely, including clients in Los Angeles and Long Beach.'
}, {
  question: 'Do you help with Yelp ads?',
  answer: 'Yes. We are a Yelp Advertising Premier Partner. We have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies to maximize your return on ad spend.'
}, {
  question: 'Can you improve how fast we respond to new leads?',
  answer: 'Absolutely. Our systems are designed to eliminate the delay between a lead reaching out and getting a response. We replace slow human follow-up with instant AI responses and missed-call text-back automation.'
}, {
  question: 'Do I need a new website to work with you?',
  answer: 'No. While we do build high-converting, mobile-first websites, they are optional conversion assets. We can often implement our AI lead conversion and ad management systems using your existing site.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1651">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-1652">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1653">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1654" />
      </button>
      <AnimatePresence data-id="element-1655">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1656">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1657">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1658">
      <SEOHead title="AI Marketing Agency Phoenix | Lead Conversion for Home Services | Viridia Analytics" description="Viridia Analytics is a Phoenix AI marketing agency helping home service businesses stop losing leads with AI lead conversion, speed-to-lead automation, and ad management." canonical="/" keywords="marketing agency phoenix, AI marketing agency, AI advertising for contractors, painters, HVAC, landscapers, contractors, window installers, AI lead conversion, local ads management, home services marketing" jsonLd={LOCAL_BUSINESS_SCHEMA} faq={faqs} data-id="element-1659" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-1660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1661">
          <div className="max-w-3xl" data-id="element-1662">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1663">
              Stop Losing Leads You{' '}
              <span className="text-viridia" data-id="element-1664">Already Paid For</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-xl sm:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-1665">
              Viridia Analytics helps home service businesses respond faster,
              follow up smarter, and turn more inbound leads into booked
              appointments - with AI-assisted automation, ad management, and
              conversion-focused systems.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-1666">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1667">
                See Where Your Leads Are Leaking
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1668" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-12 bg-viridia-surface border-y border-gray-800" data-id="element-1669">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1670">
          <ScrollReveal data-id="element-1671">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" data-id="element-1672">
              <div data-id="element-1673">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1674">242</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1675">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1676">TFP Landscaping</p>
              </div>
              <div data-id="element-1677">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1678">28</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1679">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1680">TFP Landscaping</p>
              </div>
              <div data-id="element-1681">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1682">39.9%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1683">
                  Close Rate
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1684">TFP Landscaping</p>
              </div>
              <div data-id="element-1685">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1686">138</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1687">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1688">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-1689">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1690">29</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1691">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1692">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-1693">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1694">$15.05k</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1695">
                  Closed-Won Revenue
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1696">
                  From AI-worked leads only
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 bg-viridia-black" data-id="element-1697">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1698">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-1699">
            <div className="lg:col-span-5" data-id="element-1700">
              <ScrollReveal data-id="element-1701">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1702">
                  Who We Partner With
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed mb-8" data-id="element-1703">
                  We don't work with everyone. Our systems are built
                  specifically for operators who are serious about capturing
                  demand and closing jobs.
                </p>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-7" data-id="element-1704">
              <div className="space-y-6" data-id="element-1705">
                <ScrollReveal delay={0.1} data-id="element-1706">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1707">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1708">
                      <BuildingIcon className="w-5 h-5 text-viridia" data-id="element-1709" />
                    </div>
                    <div data-id="element-1710">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-1711">
                        Local Home Service Businesses
                      </h3>
                      <p className="text-gray-400" data-id="element-1712">
                        HVAC, plumbing, landscaping, pest control, painting,
                        roofing, window installers, and general contractors.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.2} data-id="element-1713">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1714">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1715">
                      <TargetIcon className="w-5 h-5 text-viridia" data-id="element-1716" />
                    </div>
                    <div data-id="element-1717">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-1718">
                        Companies Investing in Growth
                      </h3>
                      <p className="text-gray-400" data-id="element-1719">
                        Businesses spending meaningfully on ads or actively
                        trying to increase their booked jobs.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.3} data-id="element-1720">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1721">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1722">
                      <UsersIcon className="w-5 h-5 text-viridia" data-id="element-1723" />
                    </div>
                    <div data-id="element-1724">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-1725">
                        Operators Seeking Better Systems
                      </h3>
                      <p className="text-gray-400" data-id="element-1726">
                        Owners who know they need better follow-up, faster
                        response times, and organized lead handling.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / SYSTEM */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-1727">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1728">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-1729">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1730">
              The Speed-to-Lead System
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1731">
              How we turn an inbound inquiry into a booked appointment before
              your competitor even checks their phone.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative" data-id="element-1732">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-1733"></div>

            {[{
            step: '1',
            title: 'Lead Comes In',
            desc: 'A prospect finds you via targeted ads or local search and sends a message, form, or calls.'
          }, {
            step: '2',
            title: 'Instant AI Reply',
            desc: 'Within seconds, our AI assistant responds, preventing the lead from moving to the next company.'
          }, {
            step: '3',
            title: 'Qualification',
            desc: 'The AI asks specific questions to qualify the prospect based on your actual service offerings.'
          }, {
            step: '4',
            title: 'Follow-Up',
            desc: 'If they do not book immediately, automated nurture sequences keep your business top-of-mind.'
          }, {
            step: '5',
            title: 'Job Booked',
            desc: 'The qualified estimate is scheduled directly on your calendar. You just show up and close.'
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-1734">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full" data-id="element-1735">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4 text-viridia font-bold" data-id="element-1736">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1737">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1738">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICE HIERARCHY */}
      <section className="py-24 bg-viridia-black" data-id="element-1739">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1740">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-1741">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1742">
              Our Core Infrastructure
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1743">
              We build the systems that capture demand and convert it.
            </p>
          </ScrollReveal>

          <div className="space-y-6" data-id="element-1744">
            {/* Primary Service */}
            <ScrollReveal data-id="element-1745">
              <Link to="/ai-lead-conversion" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 hover:border-viridia/50 transition-colors" data-id="element-1746">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-1747">
                  <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-1748">
                    <ZapIcon className="w-8 h-8 text-viridia" data-id="element-1749" />
                  </div>
                  <div className="flex-grow" data-id="element-1750">
                    <div className="text-xs text-viridia font-bold tracking-wide uppercase mb-1" data-id="element-1751">
                      Flagship System
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-viridia transition-colors" data-id="element-1752">
                      AI Lead Conversion & Automations
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-1753">
                      Never miss a lead. We implement AI receptionists, instant
                      text-back, and automated follow-up sequences so every
                      inquiry is handled immediately.
                    </p>
                  </div>
                  <div className="hidden md:flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full bg-gray-800 group-hover:bg-viridia transition-colors" data-id="element-1754">
                    <ArrowRightIcon className="w-5 h-5 text-white" data-id="element-1755" />
                  </div>
                </div>
              </Link>
            </ScrollReveal>

            {/* Secondary Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1756">
              <ScrollReveal delay={0.1} data-id="element-1757">
                <Link to="/ad-campaigns" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-1758">
                  <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-6" data-id="element-1759">
                    <TargetIcon className="w-6 h-6 text-red-500" data-id="element-1760" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-1761">
                    Ad Campaigns & CPC
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1762">
                    As a Yelp Advertising Premier Partner, we manage targeted ad
                    spend to drive high-intent local traffic directly into your
                    conversion system.
                  </p>
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2} data-id="element-1763">
                <Link to="/web-design-seo" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-1764">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1765">
                    <GlobeIcon className="w-6 h-6 text-blue-500" data-id="element-1766" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-1767">
                    Conversion Websites
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1768">
                    We build fast, mobile-first websites positioned as
                    conversion assets, not just digital brochures. Designed to
                    turn visitors into booked appointments.
                  </p>
                </Link>
              </ScrollReveal>
            </div>

            {/* Supporting Services */}
            <ScrollReveal delay={0.3} data-id="element-1769">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1770">
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-1771">
                  Supporting Infrastructure
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1772">
                  <Link to="/crm-business-tools" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-1773">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1774">
                      <BarChart3Icon className="w-5 h-5 text-gray-400" data-id="element-1775" />
                      <h4 className="font-bold text-white" data-id="element-1776">CRM Tools</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-1777">
                      Centralized pipeline visibility and organization.
                    </p>
                  </Link>
                  <Link to="/reputation-management" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-1778">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1779">
                      <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-1780" />
                      <h4 className="font-bold text-white" data-id="element-1781">Reputation</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-1782">
                      Automated review requests to build local trust.
                    </p>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-1783">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1784">
                      <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1785" />
                      <h4 className="font-bold text-white" data-id="element-1786">SEO / GEO</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-1787">
                      Local search optimization for discoverability.
                    </p>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* COMPARISON: AGENCIES VS VIRIDIA */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-1788">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1789">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-1790">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1791">
              What Most Agencies Sell vs.
              <br data-id="element-1792" />
              What We Actually Fix
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1793">
              We don't just send you clicks and hope for the best. We fix the
              operational gaps where revenue leaks out.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1794">
            {/* The Old Way */}
            <ScrollReveal data-id="element-1795">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-1796">
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-1797">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-1798">
                    <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-1799" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-1800">
                    Most Marketing Agencies
                  </h3>
                </div>
                <ul className="space-y-6" data-id="element-1801">
                  <li className="flex items-start gap-4" data-id="element-1802">
                    <PhoneOffIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-1803" />
                    <div data-id="element-1804">
                      <h4 className="font-bold text-white mb-1" data-id="element-1805">
                        Ignore Missed Calls
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-1806">
                        They run ads, but if you're on a jobsite and miss the
                        call, that ad spend is wasted.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1807">
                    <TrendingDownIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-1808" />
                    <div data-id="element-1809">
                      <h4 className="font-bold text-white mb-1" data-id="element-1810">
                        Send Low-Intent Traffic
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-1811">
                        They focus on cheap clicks that clog your pipeline with
                        tire-kickers instead of serious buyers.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1812">
                    <LockIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-1813" />
                    <div data-id="element-1814">
                      <h4 className="font-bold text-white mb-1" data-id="element-1815">
                        Long-Term Contracts
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-1816">
                        They lock you into 6 or 12 month retainers with zero
                        visibility into actual performance.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            {/* The Viridia Way */}
            <ScrollReveal delay={0.2} data-id="element-1817">
              <div className="bg-viridia-black border-2 border-viridia/50 rounded-2xl p-8 h-full relative overflow-hidden" data-id="element-1818">
                <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-1819"></div>
                <div className="relative z-10" data-id="element-1820">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-1821">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-1822">
                      <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-1823" />
                    </div>
                    <h3 className="text-2xl font-bold text-white" data-id="element-1824">
                      Viridia Analytics
                    </h3>
                  </div>
                  <ul className="space-y-6" data-id="element-1825">
                    <li className="flex items-start gap-4" data-id="element-1826">
                      <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1827" />
                      <div data-id="element-1828">
                        <h4 className="font-bold text-white mb-1" data-id="element-1829">
                          Instant AI Follow-Up
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-1830">
                          Our AI replies to messages and missed calls in
                          seconds, booking estimates 24/7 so leads stop
                          slipping.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-1831">
                      <BarChart3Icon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1832" />
                      <div data-id="element-1833">
                        <h4 className="font-bold text-white mb-1" data-id="element-1834">
                          Organized Pipeline
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-1835">
                          We set up a CRM that shows every lead, every job, and
                          every dollar cleanly, replacing sticky notes and
                          chaos.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-1836">
                      <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-1837" />
                      <div data-id="element-1838">
                        <h4 className="font-bold text-white mb-1" data-id="element-1839">
                          Month-to-Month Partnership
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-1840">
                          Flexible packages with real reporting. We sit between
                          you and the ad platforms to ensure transparency.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="py-24 bg-viridia-black border-b border-gray-800" data-id="element-1841">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1842">
          <ScrollReveal data-id="element-1843">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-1844">
              <div className="max-w-2xl" data-id="element-1845">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1846">
                  Documented Results
                </h2>
                <p className="text-lg text-gray-400" data-id="element-1847">
                  Real numbers from real local service businesses.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-white hover:text-gray-300 font-semibold transition-colors" data-id="element-1848">
                View Proof Library
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1849" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1850">
            <ScrollReveal delay={0.1} data-id="element-1851">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-1852">
                <div data-id="element-1853">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-1854">
                    Landscaping
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1855">
                    TFP Landscaping
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1856">
                    <strong data-id="element-1857">Challenge:</strong> 4+ hour response time meant ad
                    spend was being wasted because leads went cold before the
                    owner could reply.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1858">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1859">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1860">284%</p>
                      <p className="text-xs text-gray-400" data-id="element-1861">More Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1862">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1863">242</p>
                      <p className="text-xs text-gray-400" data-id="element-1864">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1865">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1866">28</p>
                      <p className="text-xs text-gray-400" data-id="element-1867">AI Bookings</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1868">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1869">39.9%</p>
                      <p className="text-xs text-gray-400" data-id="element-1870">Close Rate</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-1871">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1872" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1873">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-1874">
                <div data-id="element-1875">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-1876">
                    Painting
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1877">
                    Finish Coat Painting
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1878">
                    <strong data-id="element-1879">Challenge:</strong> Generating strong inbound demand
                    but couldn't keep up with conversations. Leads were slipping
                    through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1880">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1881">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1882">29</p>
                      <p className="text-xs text-gray-400" data-id="element-1883">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1884">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1885">
                        $15.05k
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-1886">Closed-Won</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1887">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1888">138</p>
                      <p className="text-xs text-gray-400" data-id="element-1889">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-1890">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1891">64%</p>
                      <p className="text-xs text-gray-400" data-id="element-1892">Lead Growth</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-1893">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1894" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1895">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1896">
          <ScrollReveal data-id="element-1897">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1898">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1899">
          <ScrollReveal delay={0.2} data-id="element-1900">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1901">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1902" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1903">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1904">
          <ScrollReveal data-id="element-1905">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-1906">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1907">
              Book a real strategy call. We'll look at your ads, website, and
              follow-up process to find exactly where you are losing jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-200 transition-all duration-300" data-id="element-1908">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-1909" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}